import React, { useCallback, useMemo, useState } from 'react';
import styles from './TrendingSongSuggestion.module.css';
import cn from 'classnames';
import SearchInput from '../../../../../ui/DataEntry/SearchInput/SearchInput';
import Button from '../../../../../ui/Buttons/Button/Button';
import { AudioPlayerProvider } from '../../../../AudioPlayer/AudioPlayerContext';
import useUrlState from '../../../../../Hooks/useUrlState';
import {
    acceptTrendingTiktokAudioSuggestion,
    getTiktokAudioAssets,
    getTrendingTiktokAudioSuggestions,
    rejectTrendingTiktokAudioSuggestion,
    TiktokAudioAsset,
    usePaginatedFetch,
} from '@round/api';
import useAbortableEffect from '../../../../../Hooks/useAbortableEffect';
import { TrendingAudioSuggestionsRow } from './TrendingAudioSuggestions.types';
import TrendingAudioSuggestionsTable from './TrendingAudioSuggestionsTable/TrendingAudioSuggestionsTable';
import { showNotification } from '../../../../../helpers';

type UrlState = {
    page: number;
    page_size: number;
    search: string;
};

const TrendingAudioSuggestions = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>({
        page: 1,
        page_size: 25,
        search: '',
    });

    const page = Number(urlState.page);
    const setPage = (page: number) => setUrlState({ page });
    const pageSize = Number(urlState.page_size);
    const setPageSize = (pageSize: number | undefined) => setUrlState({ page_size: pageSize });
    const search = urlState.search;
    const setSearch = (search: string) => setUrlState({ search, page: 1 });

    const [tableLoading, setTableLoading] = useState(false);
    const {
        page: suggestionPage,
        getCurrentPage,
        count,
    } = usePaginatedFetch(getTrendingTiktokAudioSuggestions, {
        page,
        search,
        page_size: pageSize,
    });

    const [assets, setAssets] = useState<TiktokAudioAsset[]>([]);
    const [suggestionsToFilter, setSuggestionsToFilter] = useState<number[]>([]);

    const rows: TrendingAudioSuggestionsRow[] = useMemo(
        () =>
            suggestionPage
                .filter((s) => !suggestionsToFilter.includes(s.id))
                .map((s) => {
                    const asset = assets.find((a) => a.audio_id === s.audio_id);
                    return {
                        ...s,
                        imageUrl: asset?.image_thumb.cached_url || asset?.image_thumb.original_url || '',
                        audioUrl: asset?.audio_file.cached_url || asset?.audio_file.original_url || '',
                    };
                }),
        [assets, suggestionPage, suggestionsToFilter]
    );

    useAbortableEffect(
        (signal) => {
            setTableLoading(true);
            getCurrentPage({ signal });
        },
        [getCurrentPage]
    );

    useAbortableEffect(
        (signal) => {
            if (suggestionPage.length) {
                const audioIds = suggestionPage.map((s) => s.audio_id);
                getTiktokAudioAssets(audioIds, { signal })
                    .then(setAssets)
                    .catch(() => {})
                    .finally(() => setTableLoading(false));
            }

            if (count === 0) {
                setTableLoading(false);
            }
        },
        [suggestionPage, count]
    );

    const handleAccept = useCallback(async (suggestion: TrendingAudioSuggestionsRow) => {
        try {
            await acceptTrendingTiktokAudioSuggestion(suggestion.id);
            showNotification('Accepted', 'info');
            setSuggestionsToFilter((s) => s.concat(suggestion.id));
        } catch {
            showNotification('Could not add audio', 'error');
        }
    }, []);

    const handleReject = useCallback(async (suggestion: TrendingAudioSuggestionsRow) => {
        try {
            await rejectTrendingTiktokAudioSuggestion(suggestion.id);
            showNotification('Rejected', 'info');
            setSuggestionsToFilter((s) => s.concat(suggestion.id));
        } catch {
            showNotification('Could not reject audio', 'error');
        }
    }, []);

    return (
        <div className={styles.container}>
            <div className={cn(styles.row, styles.spaceBetween)}>
                <div className={styles.outstandingSuggestions}>{count - suggestionsToFilter.length} outstanding</div>
                <div className={cn(styles.row, styles.actions)}>
                    <SearchInput className={styles.search} value={urlState.search} onChange={setSearch} />
                    <Button
                        disabled={tableLoading}
                        type="filled"
                        className={styles.refreshTableButton}
                        onClick={() => {
                            setTableLoading(true);
                            getCurrentPage();
                        }}
                    >
                        Refresh table
                    </Button>
                </div>
            </div>

            <AudioPlayerProvider>
                <TrendingAudioSuggestionsTable
                    loading={tableLoading}
                    data={rows}
                    count={count}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    onAcceptSuggestionClicked={handleAccept}
                    onRejectSuggestionClicked={handleReject}
                />
            </AudioPlayerProvider>
        </div>
    );
};

export default TrendingAudioSuggestions;
